import type { PjTxMap } from '../components/projects/pjTx';
import { kstWeekLabel } from '../../server/core/pjtPure';   // 🗓 주차 이름은 서버 잡과 공용 (2026-10-05)   // 🌐 프로젝트 다국어 번역본 맵 (2026-09-10)
import i18next from 'i18next';   // 📏 업로드 용량 오류 문구 (2026-10-14)
// 🗂 프로젝트 협업 API (P1, 2026-08-29 — docs/PJT-협업-설계-20260829.md)
//   사이드카 /api/tpt/projects — 인증 헤더는 installTptAuthFetch 가 자동으로 싣는다 (GUIDE §사이드카 호출).
//   ⚠️ 기존 api 의 방 귀속 WBS(/api/tpt/project)와 별개.

// setup = 셋업/설치(SETUP·INSTALL 통합, 2026-11-04) — 단계 4개(kind schedule/progress/diary/ncr), server/core/setupPure
export type PjType = 'recurring' | 'phased' | 'flat' | 'setup';
export type PjRole = 'pm' | 'member' | 'watcher';
export type CardType = 'doc' | 'post';
export type CardStatus = 'todo' | 'doing' | 'done';
// 🧭 카드 우선순위 (2026-09-06 PJT 1차 A — 잔디 PMS 명세). 없으면 normal 로 본다.
export type PjPriority = 'urgent' | 'high' | 'normal' | 'low';
/** 체크리스트 1줄 — update 시 **배열 전체 교체**(서버 계약 §1) */
export interface PjChecklistItem { id: string; text: string; done: boolean; order: number }
/** 참조자(watcher) — 프로젝트 멤버 중에서만, 담당자와 중복 불가(서버가 정규화) */
export interface PjWatcher { userId: string; username: string; name?: string }
/** 카드 수정 이력 1줄 — audit detail.changes (서버 계약 §3-1) */
export interface PjChange { field: string; from: string; to: string }

/** 🧭 우선순위 메타 — 배지·칩·필터·리스트가 모두 여기 하나를 본다 (2026-09-06).
 *  label 은 폴백(한국어), 화면 문구는 `t(k)` 로 5종 로케일을 쓴다. */
export const PRIORITY_META: Record<PjPriority, { label: string; k: string; chip: string; dot: string }> = {
	urgent: { label: '긴급', k: 'pj.prio_urgent', chip: 'bg-red-50 text-red-600', dot: 'bg-red-500' },
	high: { label: '높음', k: 'pj.prio_high', chip: 'bg-orange-50 text-orange-600', dot: 'bg-orange-500' },
	normal: { label: '보통', k: 'pj.prio_normal', chip: 'bg-blue-50 text-class-blue', dot: 'bg-class-blue' },
	low: { label: '낮음', k: 'pj.prio_low', chip: 'bg-surface-muted text-fg-subtle', dot: 'bg-slate-400' },
};
/** 표시 순서 — 칩 나열·필터 드롭다운 공용 (정렬 순위는 pjFilters.PRIORITY_RANK) */
export const PRIORITY_KEYS: PjPriority[] = ['urgent', 'high', 'normal', 'low'];

export interface TptProject {
	_id: string;
	name: string;
	type: PjType;
	/** 🔧 셋업 진행률 항목(유닛별·장비 전체) — setup 유형만 */
	setup?: { unitItems: string[]; equipItems: string[] };
	color: string;
	status: 'active' | 'done' | 'hold';
	pmId: string;
	pmUsername: string;
	pmName?: string;
	startAt?: string | null;
	endAt?: string | null;
	mySort?: number | null;
	archivedAt?: string | null;
	cardDisplay?: 'auto' | 'person' | 'title';
	// 🧭 1차 A (2026-09-06) — 설명(≤500) · 종료(읽기 전용) · 내 즐겨찾기 플래그
	description?: string;
	closedAt?: string | null;
	favorite?: boolean;
	// 🔑 업무 키 (2026-09-06 2차-B) — 'MT' 2~4자 대문자·숫자, 활성 프로젝트 내 유일. 구 데이터엔 없다(마이그레이션 전).
	key?: string;
	/** 🏷 태그 (2026-10-02) — 최대 5개, '#' 없이 저장. 메인 검색·카드 표시 */
	tags?: string[];
	/** 카드 번호 시퀀스 — 서버가 $inc 로 올린다(클라는 읽기만) */
	cardSeq?: number;
	/** 📅 캘린더 표시 범위 — 'all'(멤버 전원, 기본) / 'involved'(담당·참조자·PM 만) */
	calendarScope?: 'all' | 'involved';
	/** 🔕 대화방 알림 — 카드·상태 변경을 연결된 방에 게시할지. 필드 없음 = 켜짐(기존 프로젝트 동작 유지) (2026-09-14) */
	roomNotify?: boolean;
	backingRid: string;
	roomIds: string[];
	/** 🌐 프로젝트 언어(2개 이상이면 서버가 저장 시 자동 번역, components/projects/pjTx) */
	langs?: string[];
	tx?: PjTxMap;
	createdAt: string;
	updatedAt: string;
	// 목록 응답에서만 채워지는 집계
	stageCount?: number;
	cardCount?: number;
	doneCardCount?: number;
	todoCardCount?: number;   // 예정(지연 제외) — 전체 대시보드용 (2026-08-30)
	doingCardCount?: number;  // 진행(지연 제외)
	lateCardCount?: number;   // 지연 = 미완료 + 기한 경과
	memberCount?: number;
	watcherCount?: number;
	myRole?: PjRole | null;
}

export interface TptStage { _id: string; pid: string; order: number; name: string; kind: 'week' | 'phase' | 'default' | 'schedule' | 'progress' | 'diary' | 'ncr'; startAt?: string | null; dueAt?: string | null; tx?: PjTxMap }
export interface TptCard {
	_id: string; pid: string; sid: string; type: CardType; title: string; status: CardStatus; order: number;
	tx?: PjTxMap;   // 🌐 title/bodyText 번역본
	ownerId: string; ownerUsername: string; ownerName?: string; dueAt?: string | null; createdAt: string; updatedAt: string;
	owners?: Array<{ userId: string; username: string; name?: string }>;
	curVerName?: string;
	commentCount?: number;
	threadUnread?: boolean;
	curVer?: number; rootMsgId?: string;
	// 📎 여러 파일 (2026-10-07) — curRound=최신 회차 · fileCount=현재 파일 수. 구 카드엔 없음 → 표시는 pjVerBadge 가 curVer 로 대신한다
	curRound?: number; fileCount?: number;
	shares?: Array<{ rid: string; fname?: string; msgId: string; by: string; byId?: string; at: string }>;
	// 📝 카드 글(리치 본문, 2026-08-31) — 목록엔 파생값만 내려온다(body 는 GET /post 단건 조회)
	bodyLen?: number; bodyText?: string; bodyTplName?: string; bodyUpdatedAt?: string; bodyUpdatedBy?: string;
	// 🧭 1차 A (2026-09-06) — 우선순위·시작일·체크리스트·참조자·보관. 구 데이터는 전부 없음(기본값 취급)
	priority?: PjPriority;
	startAt?: string | null;
	checklist?: PjChecklistItem[];
	watchers?: PjWatcher[];
	archivedAt?: string | null;
	copiedFrom?: string;
	createdBy?: string;
	// 🔑 카드 번호 (2026-09-06 2차-B) — 프로젝트 안에서 1부터. 표시 키는 `cardKey(project, card)` = 'MT-12'
	no?: number;
	/** 🔧 셋업 단계 카드의 구조화 입력값(일정표 실행일·진행률·셋업일지·부적합) — kind 별 정규화는 server/core/setupPure */
	form?: Record<string, unknown>;
}
export interface TptMember { pid: string; userId: string; username: string; name?: string; role: PjRole; source?: string; at?: string }
// byName — 행위자 실명 (2026-09-06, /activity 가 namesByIds 로 채워 준다). detail.changes 는 parseChanges 로 읽는다.
export interface TptActivity { action: string; by: string; byName?: string; at: string; detail?: Record<string, unknown>; ctx?: { card?: string; stage?: string } }

export interface PjRoom { rid: string; fname: string; t?: string; isBacking?: boolean }
export interface ProjectDetail { project: TptProject; stages: TptStage[]; cards: TptCard[]; members: TptMember[]; activity: TptActivity[]; rooms?: PjRoom[]; myRole: PjRole | null }

const BASE = '/api/tpt/projects';

async function req<T>(url: string, init?: RequestInit): Promise<T> {
	const r = await fetch(url, init && init.body ? { ...init, headers: { 'Content-Type': 'application/json', ...(init.headers || {}) } } : init);
	const j = await r.json().catch(() => ({}));
	if (!r.ok) throw new Error((j as { error?: string }).error || `HTTP ${r.status}`);
	return j as T;
}

export const listProjects = (all?: boolean, archived: 'active' | 'archived' | 'all' = 'active') => req<{ projects: TptProject[] }>(`${BASE}?archived=${archived}${all ? '&all=1' : ''}`).then((r) => r.projects);
/** 👑 목록 + 소유자 플래그 (2026-09-10) — owner 가 true 인 계정만 '모든 프로젝트 보기'(all) 를 쓸 수 있다. 서버가 소유자 아니면 all 을 무시한다. */
export const listProjectsWithMeta = (all: boolean, archived: 'active' | 'archived' | 'all' = 'active') => req<{ projects: TptProject[]; owner?: boolean }>(`${BASE}?archived=${archived}${all ? '&all=1' : ''}`);
// 전체 대시보드용 — projects + 고유 참여 인원 수(peopleCount, 구 사이드카는 undefined)
export const listProjectsFull = (archived: 'active' | 'archived' | 'all' = 'active') => req<{ projects: TptProject[]; peopleCount?: number }>(`${BASE}?archived=${archived}`);
export const getProject = (pid: string) => req<ProjectDetail>(`${BASE}/one?id=${encodeURIComponent(pid)}`);
// key — 없으면 서버가 이름에서 자동 제안(suggestProjectKey). 중복이면 409 (2026-09-06 2차-B)
export const createProject = (body: { name: string; type: PjType; color?: string; startAt?: string | null; endAt?: string | null; stages?: string[]; key?: string; langs?: string[]; memberUsernames?: Array<{ username: string; userId: string; name?: string }> }) =>
	// key — 서버가 실제 부여한 키(자동 제안·충돌 회피 결과)를 돌려준다
	req<{ ok: boolean; pid: string; backingRid: string; key?: string }>(BASE, { method: 'POST', body: JSON.stringify(body) });
// description(빈 문자열이면 서버가 $unset) · closed(종료/다시 열기) 추가 — 2026-09-06 1차 A
// key(업무 키) · calendarScope(캘린더 표시 범위) 추가 — 2026-09-06 2차-B. 둘 다 PM 전용
export const updateProject = (pid: string, body: { name?: string; color?: string; status?: 'active' | 'done' | 'hold'; startAt?: string | null; endAt?: string | null; archived?: boolean; cardDisplay?: 'auto' | 'person' | 'title'; description?: string; closed?: boolean; key?: string; calendarScope?: 'all' | 'involved'; roomNotify?: boolean; tags?: string[]; setup?: { unitItems: string[]; equipItems: string[] } }) =>
	req<{ ok: boolean }>(`${BASE}/one?id=${encodeURIComponent(pid)}`, { method: 'PATCH', body: JSON.stringify(body) });
/** 📁 내 프로젝트 폴더 (개인, 2026-09-08) — 서버 tpt_project_userfolders + localStorage 캐시. 순수 조작은 components/projects/pjFolders.ts */
export interface PjFolders { folders: Array<{ id: string; name: string }>; map: Record<string, string> }
export const PJ_FOLDERS_LS = 'tpt-pjt-folders';
export const getMyPjFolders = () => req<PjFolders>(`${BASE}/my-folders`);
export const saveMyPjFolders = (data: PjFolders) => req<{ ok: boolean } & PjFolders>(`${BASE}/my-folders`, { method: 'PUT', body: JSON.stringify(data) });
/** ☆ 즐겨찾기 — 내 멤버십에만 저장(tpt_project_members.favorite). 홈 목록 상단 고정에 쓴다 (2026-09-06) */
export const setFavorite = (pid: string, on: boolean) =>
	req<{ ok: boolean }>(`${BASE}/favorite`, { method: 'POST', body: JSON.stringify({ pid, on }) });
// 🗑 영구 삭제 — PM 전용. 단계·카드·산출물까지 전부, 전용 토픽(대화방)은 남는다 (2026-08-31 원격 #2)
/** withRoom — 전용 토픽(대화방)까지 삭제 (2026-09-04). 서버가 PM 토큰→admin 순으로 groups.delete, 결과는 roomDeleted */
export const deleteProject = (pid: string, withRoom?: boolean) =>
	req<{ ok: boolean; roomDeleted?: boolean }>(`${BASE}/one?id=${encodeURIComponent(pid)}${withRoom ? '&room=1' : ''}`, { method: 'DELETE' });

export const stageAction = (body: { pid: string; action: 'add' | 'rename' | 'reorder' | 'remove' | 'copy-cards'; sid?: string; fromSid?: string; name?: string; startAt?: string | null; dueAt?: string | null; orders?: Array<{ sid: string; order: number }> }) =>
	req<{ ok: boolean; stage?: TptStage; copied?: number; skipped?: number }>(`${BASE}/stage`, { method: 'POST', body: JSON.stringify(body) });

// duplicate(복사) · move-project(다른 프로젝트로 이동) · archive/unarchive(보관) + 신규 필드 — 2026-09-06 1차 A
export const cardAction = (body: {
	pid: string; action: 'add' | 'update' | 'move' | 'remove' | 'reorder' | 'duplicate' | 'move-project' | 'archive' | 'unarchive';
	cid?: string; sid?: string; title?: string; type?: CardType; ownerId?: string; ownerUsername?: string; ownerName?: string;
	owners?: Array<{ userId: string; username?: string; name?: string }>; dueAt?: string | null; status?: CardStatus; order?: number;
	orders?: Array<{ cid: string; sid?: string; order: number }>;
	priority?: PjPriority; startAt?: string | null; watchers?: Array<{ userId: string; username?: string; name?: string }>; checklist?: PjChecklistItem[];
	toPid?: string; toSid?: string;
	form?: Record<string, unknown>;
}) =>
	// filesMoved — move-project 만 내려준다. false = 카드는 옮겼지만 산출물 파일 폴더 이동 실패(호출부가 안내)
	// no — 새로 부여된 카드 번호. move-project 는 **대상 프로젝트의 새 번호**(원본 번호는 폐기, 2026-09-06 2차-B)
	req<{ ok: boolean; card?: TptCard; filesMoved?: boolean; no?: number }>(`${BASE}/card`, { method: 'POST', body: JSON.stringify(body) });

export const memberAction = (body: { pid: string; action: 'add' | 'remove' | 'role'; userId?: string; username?: string; name?: string; role?: PjRole }) =>
	req<{ ok: boolean }>(`${BASE}/member`, { method: 'POST', body: JSON.stringify(body) });

// cid — 그 카드의 기록만 (카드 모달 '기록' 탭, 2026-09-06). days=0 이면 전체 기간.
export const listActivity = (pid: string, opts?: { limit?: number; days?: number; cid?: string }) =>
	req<{ activity: TptActivity[] }>(`${BASE}/activity?id=${encodeURIComponent(pid)}&limit=${opts?.limit ?? 200}&days=${opts?.days ?? 7}${opts?.cid ? `&cid=${encodeURIComponent(opts.cid)}` : ''}`).then((r) => r.activity);
/** 계약서(§4-1)상 이름 — 기존 호출부(HistoryModal)는 listActivity 를 그대로 쓴다 */
export const fetchActivity = listActivity;

export const saveProjectOrder = (orders: Array<{ pid: string; order: number }>) =>
	req<{ ok: boolean }>(`${BASE}/order`, { method: 'POST', body: JSON.stringify({ orders }) });

/** 🌐 프로젝트 언어 편집 (PM) — retranslate 면 기존 이름·단계·카드·글을 서버가 백그라운드로 다시 번역 (2026-09-10) */
export const setProjectLangs = (pid: string, langs: string[], retranslate: boolean) =>
	req<{ ok: boolean; langs: string[]; retranslating: boolean }>(`${BASE}/langs`, { method: 'POST', body: JSON.stringify({ pid, langs, retranslate }) });
export const linkAction = (body: { pid: string; action: 'link' | 'unlink'; rid: string }) =>
	req<{ ok: boolean }>(`${BASE}/link`, { method: 'POST', body: JSON.stringify(body) });

// ── P2 — 산출물 버전 · 카드 스레드 · 방 공유 · 메시지 등록 ──────────────
// 📎 slot=파일 줄 · rev=그 줄의 개정 · round=회차 (2026-10-07 여러 파일 — 정본 server/core/pjtVersionsPure). 서버가 구 데이터도 채워 내려준다
export interface TptVersion { _id: string; pid: string; cid: string; ver: number; name: string; size: number; mime: string; note?: string; restoredFrom?: number; by: string; byName?: string; at: string; slot: string; rev: number; round: number; replace?: boolean }
// 📎 attachments = RC 첨부 형태 그대로 (2026-08-30 원격 #3 — 스레드가 RC 메시지 기반이라 업로드 첨부가 실림)
export interface TptCommentAtt { title?: string; title_link?: string; image_url?: string; image_type?: string; type?: string; size?: number }
export interface TptComment { _id: string; msg: string; ts: string; u?: { _id?: string; username?: string; name?: string }; tx?: string; reactions?: Record<string, { usernames?: string[] }>; attachments?: TptCommentAtt[] }

export const listVersions = (cid: string) => req<{ versions: TptVersion[]; excludedSlots?: string[] }>(`${BASE}/versions?cid=${encodeURIComponent(cid)}`).then((r) => ({ versions: r.versions || [], excludedSlots: r.excludedSlots || [] }));
export const versionDownloadUrl = (cid: string, ver: number) => `${BASE}/version?cid=${encodeURIComponent(cid)}&ver=${ver}`;
/** 업로드 1건. opts — round: 'new'(새 회차) | 'last'(방금 내가 올린 회차에 합류) · slot: [교체]할 파일 줄 · notify: 묶음의 마지막 파일에서만 방 알림 */
//   📏 용량 오류 문구 (2026-10-14 형 "넘어갈 때 오류 메시지도 수정") — 207MB pptx 가 nginx 상한에 막혀 연결이 끊기자 화면엔
//   "네트워크 연결을 확인하세요" 만 떴다. 서버(nginx) 413 이면 용량 안내로, 큰 파일 전송 중 연결이 끊기면(fetch TypeError) 그 사실을 알린다.
//   사이드카 413 은 자체 문구(관리자 첨부 한도 MB)를 그대로 보여 준다.
const fmtMB = (n: number) => `${(n / 1024 / 1024).toFixed(n >= 100 * 1024 * 1024 ? 0 : 1)}MB`;
export const uploadVersion = async (pid: string, cid: string, file: File, note?: string, opts?: { round?: 'new' | 'last'; slot?: string; notify?: boolean }) => {
	try {
		return await req<{ ok: boolean; version: TptVersion }>(
			`${BASE}/version?pid=${encodeURIComponent(pid)}&cid=${encodeURIComponent(cid)}&name=${encodeURIComponent(file.name)}&mime=${encodeURIComponent(file.type || 'application/octet-stream')}${note ? `&note=${encodeURIComponent(note)}` : ''}&round=${opts?.round || 'new'}${opts?.slot ? `&slot=${encodeURIComponent(opts.slot)}` : ''}${opts?.notify === false ? '' : '&notify=1'}`,
			{ method: 'PUT', body: file });
	} catch (e) {
		const msg = String((e as Error)?.message || '');
		if (/HTTP 413/.test(msg)) throw new Error(i18next.t('pj.uploadTooLarge', { defaultValue: "'{{name}}'({{size}})은(는) 서버가 받을 수 있는 크기를 넘습니다. 파일을 줄이거나 대화방에 올린 뒤 링크로 등록해 주세요.", name: file.name, size: fmtMB(file.size) }));
		if (e instanceof TypeError && file.size > 50 * 1024 * 1024) throw new Error(i18next.t('pj.uploadDropped', { defaultValue: "'{{name}}'({{size}}) 업로드 중 연결이 끊겼습니다. 큰 파일이면 잠시 후 다시 시도하거나 파일을 줄여 주세요.", name: file.name, size: fmtMB(file.size) }));
		throw e;
	}
};
export const restoreVersion = (pid: string, cid: string, ver: number) =>
	req<{ ok: boolean; version: TptVersion }>(`${BASE}/version-restore`, { method: 'POST', body: JSON.stringify({ pid, cid, ver }) });
/** 🙈 파일 줄을 현재 산출물에서 제외(off=true) / 되살리기(off=false) — 이력·파일은 그대로 */
export const excludeVersionSlot = (pid: string, cid: string, slot: string, off: boolean) =>
	req<{ ok: boolean; excludedSlots: string[] }>(`${BASE}/version-exclude`, { method: 'POST', body: JSON.stringify({ pid, cid, slot, off }) });
/** 🔗 기존 파일 대체 — from 줄을 into 줄의 새 개정으로 합친다(into 의 현재 파일 = from 최신본) (2026-11-02) */
export const mergeVersionSlot = (pid: string, cid: string, from: string, into: string) =>
	req<{ ok: boolean }>(`${BASE}/version-merge`, { method: 'POST', body: JSON.stringify({ pid, cid, from, into }) });
/** 🏷 카드 산출물 배지 — 'v3'(회차) · 파일이 여럿이면 'v3 · 📎2'. 산출물 없으면 '' (구 카드는 curRound 가 없어 curVer=회차) */
export const pjVerBadge = (c: { curVer?: number; curRound?: number; fileCount?: number }): string => {
	if (!(c.curVer || 0)) return '';
	const r = c.curRound || c.curVer || 0;
	return (c.fileCount || 0) > 1 ? `v${r} · 📎${c.fileCount}` : `v${r}`;
};

// ── 📝 카드 글(리치 본문) + 관리자 템플릿 (2026-08-31 사용자 "템플릿 폼에 글 작성") ──────────
export interface CardPost { html: string; tplId?: string; tplName?: string; updatedAt?: string | null; by?: string; byName?: string; /** 🌐 lang → 글 본문 평문 번역 */ tx?: Record<string, string> }
export interface PjPostTemplate { _id: string; name: string; html: string; active?: boolean; order?: number; updatedAt?: string }
/** 🔧 셋업 카드 form 전체(그림 포함) — 목록(/one)은 리치 칸 그림을 비우고 form._lazy 로 내려준다 (2026-11-04) */
export const getCardForm = (pid: string, cid: string) => req<{ form: Record<string, unknown> }>(`${BASE}/card-form?pid=${encodeURIComponent(pid)}&cid=${encodeURIComponent(cid)}`).then((r) => r.form || {});
export const getCardPost = (cid: string) => req<CardPost>(`${BASE}/post?cid=${encodeURIComponent(cid)}`);
export const saveCardPost = (pid: string, cid: string, html: string, tpl?: { id?: string; name?: string }) =>
	req<{ ok: boolean }>(`${BASE}/post`, { method: 'PUT', body: JSON.stringify({ pid, cid, html, tplId: tpl?.id || '', tplName: tpl?.name || '' }) });
/** 📜 글 이전본 (2026-10-22) — 저장 때마다 덮어쓴 직전 본문, 카드당 최근 5개. 목록엔 본문이 없고 단건 조회로 받는다 */
export interface PostHistoryItem { id: string; savedAt: string | null; archivedAt: string | null; savedByName: string; bodyLen: number; preview: string }
export const listPostHistory = (pid: string, cid: string) =>
	req<{ items: PostHistoryItem[] }>(`${BASE}/post-history?pid=${encodeURIComponent(pid)}&cid=${encodeURIComponent(cid)}`).then((r) => r.items || []);
export const getPostHistory = (pid: string, cid: string, id: string) =>
	req<{ id: string; html: string; bodyLen: number; savedAt: string | null; archivedAt: string | null; savedByName: string }>(`${BASE}/post-history?pid=${encodeURIComponent(pid)}&cid=${encodeURIComponent(cid)}&id=${encodeURIComponent(id)}`);
/** 이 버전으로 되돌리기 — 현재 글도 이전본에 남는다(되돌리기의 되돌리기 가능) */
export const restorePostHistory = (pid: string, cid: string, id: string) =>
	req<{ ok: boolean; html: string; updatedAt: string }>(`${BASE}/post-restore`, { method: 'POST', body: JSON.stringify({ pid, cid, id }) });
export const listPostTemplates = (all?: boolean) => req<{ templates: PjPostTemplate[] }>(`${BASE}/templates${all ? '?all=1' : ''}`).then((r) => r.templates);
export const postTemplateAction = (body: { action: 'add' | 'update' | 'remove'; id?: string; name?: string; html?: string; active?: boolean; order?: number }) =>
	req<{ ok: boolean; template?: PjPostTemplate }>(`${BASE}/templates`, { method: 'POST', body: JSON.stringify(body) });

export const getThread = (pid: string, cid: string, lang?: string) => req<{ comments: TptComment[]; rootMsgId?: string; rid?: string }>(`${BASE}/thread?pid=${encodeURIComponent(pid)}&cid=${encodeURIComponent(cid)}${lang ? `&lang=${encodeURIComponent(lang)}` : ''}`);
export const postComment = (pid: string, cid: string, text: string) =>
	req<{ ok: boolean; msgId: string }>(`${BASE}/thread`, { method: 'POST', body: JSON.stringify({ pid, cid, text }) });
// 📎 첨부 업로드 전 루트 확보 (2026-08-30 원격 #3) — 첫 댓글이 파일이면 루트가 없어 tmid 업로드 불가
export const ensureThreadRoot = (pid: string, cid: string) =>
	req<{ ok: boolean; rootMsgId: string; rid: string }>(`${BASE}/thread-root`, { method: 'POST', body: JSON.stringify({ pid, cid }) });

export const shareCard = (pid: string, cid: string, rid: string) =>
	req<{ ok: boolean; watcherAdded: number }>(`${BASE}/share`, { method: 'POST', body: JSON.stringify({ pid, cid, rid }) });
export interface PjRoomChip { pid: string; name: string; color: string; isBacking?: boolean; tx?: PjTxMap }   // tx = 이름 번역 (2026-09-29)
export const byRoomProjects = (rid: string) =>
	req<{ projects: PjRoomChip[] }>(`${BASE}/by-room?rid=${encodeURIComponent(rid)}`).then((r) => r.projects);

/** 홈 위젯 요약 카드(마감 가까운 8건) — 2차-A 에서 `PjMyCard`(내 업무 화면 카드)와 이름이 겹쳐 개명 */
// key/no — 업무 키 (2026-09-06 2차-B). 구 사이드카 응답엔 없다
// tx = 카드 제목 번역본 · pjTx = 프로젝트명 번역본 (2026-09-28 홈 위젯 다국어 — 구 사이드카엔 없다 → 원문)
export interface PjSummaryCard { pid: string; pjName: string; cid: string; title: string; type?: CardType; status?: string; dueAt?: string | null; overdue: boolean; key?: string; no?: number; tx?: PjTxMap; pjTx?: PjTxMap }
// 📅 이번 주 나의 업무 (2026-09-06 2차-A) — KST 일~토. 구 사이드카는 week 자체가 없다(홈 위젯이 종전 목록으로 폴백).
export interface PjWeekDay { date: string; total: number; done: number; overdue: number }
export interface PjWeekCard { cid: string; pid: string; pjName: string; title: string; status?: string; priority?: PjPriority; dueAt?: string | null; overdue: boolean; key?: string; no?: number; tx?: PjTxMap; pjTx?: PjTxMap }
export interface PjWeek { start: string; days: PjWeekDay[]; cards: PjWeekCard[] }
// key/no — 업무 키 표시용 (2026-09-06 2차-B). 구 사이드카 응답엔 없다 → 라벨을 그리지 않는다
export interface PjMySummary { projects: Array<{ pid: string; name: string; color: string; status?: string; myRole: PjRole; total: number; done: number; key?: string; tx?: PjTxMap }>; cards: PjSummaryCard[]; overdue: number; dueSoon: number; open: number; week?: PjWeek }
export const mySummary = () => req<PjMySummary>(`${BASE}/my-summary`);

// ── 🧑‍💼 내 업무 (2026-09-06 2차-A) — 내가 담당(owner)이거나 참조(watcher)된 카드 전부 ────────
/** 카드의 나와의 관계 — 서버가 relationOf(card, me) 로 계산해 내려준다 */
export type PjMyRelation = 'owner' | 'watcher' | 'both';
export type PjMyCard = TptCard & { pjName: string; pjColor: string; pjClosed: boolean; stageName?: string; myRelation: PjMyRelation };
export interface PjMyCardsResp {
	cards: PjMyCard[];
	/** 카드가 있는 프로젝트만 (key = 업무 키 — 구 사이드카는 undefined) */
	projects: Array<{ pid: string; name: string; color: string; closedAt?: string | null; key?: string; tx?: PjTxMap }>;   // tx = 이름 번역 (2026-09-28)
	/** 위 카드들의 단계 (리스트 뷰 단계 열·정렬용) */
	stages: TptStage[];
	counts: { total: number; owner: number; watcher: number; overdue: number };
}
/** scope 기본 all · archived=1 이면 보관 카드 포함. 완료 카드는 서버가 항상 내려주고 걸러내기는 클라 필터가 한다 */
export const myCards = (params?: { scope?: 'all' | 'owner' | 'watcher'; archived?: boolean }) =>
	req<PjMyCardsResp>(`${BASE}/my-cards?scope=${params?.scope || 'all'}${params?.archived ? '&archived=1' : ''}`);

// ── 🛠 관리자 전사 대시보드 (2026-09-06 2차-C) — `GET /admin-overview`, 서버 requireAdmin ────────
//   집계는 전부 서버(순수 함수 aggregateAdminOverview + 60s 캐시) — 클라는 그리기만 한다.
// 🔴 값 형태는 서버 확정본(2026-09-06) — 문자열 필드는 **없으면 undefined 가 아니라 ''**, 수치는 항상 온다.
/** KPI — 상태는 done+doing+todo = total 3분할이고, overdue 는 그와 **겹치는 별도 축**(미완료 + 마감 경과)이다.
 *  그래서 도넛은 3분할로만 그리고 지연은 KPI 타일로만 보여 준다. */
export interface PjAdminKpi {
	cards: { total: number; done: number; doing: number; todo: number; overdue: number };
	/** archived 는 항상 온다 — '보관 포함' 이 아니면 0 */
	projects: { active: number; closed: number; archived: number };
	/** 완료율 done/total (0~1, 반올림 전 — 표시는 Math.round(v*100)). 총 0 이면 0 */
	completion: number;
}
export interface PjAdminProject {
	/** key·pmName·color 는 없으면 '' */
	pid: string; key: string; name: string; color: string; pmName: string; closed: boolean;
	/** total 0 인 행도 온다 — 진행률 계산 시 0 나누기 방어 필요 */
	total: number; done: number; doing: number; todo: number; overdue: number; members: number;
	/** ISO 문자열, 없으면 '' */
	updatedAt: string;
}
/** 이름은 서버 namesByIds 로 채워진다(계정 ID 비노출 규약) */
export interface PjAdminMember { userId: string; username: string; name: string; total: number; done: number; overdue: number }
/** dueAt 은 **KST 일자 'YYYY-MM-DD'**(ISO 아님) · ownerNames 는 이름만(못 찾은 담당자는 빠진다) */
export interface PjAdminOverdueCard { pid: string; key: string; no: number; cid: string; title: string; dueAt: string; ownerNames: string[]; pjName: string }
export interface PjAdminOverview {
	kpi: PjAdminKpi;
	byProject: PjAdminProject[];
	byMember: PjAdminMember[];
	overdueCards: PjAdminOverdueCard[];
	/** 회사 필터 옵션 — 관리자 대시보드 metrics.companies 와 같은 형식. 요청한 company 는 되돌려주지 않는다(클라 상태 유지) */
	companies: Array<{ id: string; name: string }>;
	/** 항상 온다 — 미지정 축은 '' */
	range: { from: string; to: string };
	/** 카드 20000 · 프로젝트 2000 초과 — 집계가 일부만 반영됐다는 표시 */
	truncated: boolean;
	/** 60s 캐시 생성 시각 (ISO 문자열) */
	cachedAt: string;
}
/** company '' = 전체 | 회사 id | 'associate' · from/to 'YYYY-MM-DD'(KST, 둘 다 없으면 전체 기간) */
export const adminOverview = (params?: { company?: string; from?: string; to?: string; archived?: boolean }) => {
	const q = new URLSearchParams();
	if (params?.company) q.set('company', params.company);
	if (params?.from) q.set('from', params.from);
	if (params?.to) q.set('to', params.to);
	if (params?.archived) q.set('archived', '1');
	const s = q.toString();
	return req<PjAdminOverview>(`${BASE}/admin-overview${s ? `?${s}` : ''}`);
};

export const filterMemberCandidates = (usernames: string[]) =>
	req<{ usernames: string[] }>(`${BASE}/member-candidates`, { method: 'POST', body: JSON.stringify({ usernames }) }).then((r) => new Set(r.usernames));

export const unshareCard = (pid: string, cid: string, msgId: string) =>
	req<{ ok: boolean }>(`${BASE}/unshare`, { method: 'POST', body: JSON.stringify({ pid, cid, msgId }) });

export interface PjThreadItem { pid: string; pjName: string; cid: string; title: string; type?: CardType; status?: string; stageName?: string; ownerName?: string; curVer?: number; curRound?: number; fileCount?: number; myRole?: PjRole; count: number; lastAt: string; lastMsg: string; lastBy: string; tx?: PjTxMap; pjTx?: PjTxMap; stageTx?: PjTxMap }   // 🌐 번역(2026-09-28)
// 🌐 프로젝트 토픽 이름 번역 (2026-09-28) — components/projects/pjRoomNames 가 캐시
export const listPjRoomNames = () => req<{ rooms: Array<{ rid: string; name?: string; tx: PjTxMap }> }>(`${BASE}/room-names`).then((r) => r.rooms || []);
export const listCardThreads = () => req<{ threads: PjThreadItem[] }>(`${BASE}/threads`).then((r) => r.threads);

// 📎 산출물 목록 (2026-08-30 원격 #13) — 내 프로젝트 카드의 최신 산출물(우측 탭 뷰)
export interface PjDeliverable { pid: string; pjName: string; cid: string; title: string; stageName?: string; ownerName?: string; status?: string; ver: number; fileName: string; at?: string; tx?: PjTxMap; pjTx?: PjTxMap; stageTx?: PjTxMap }   // 🌐 번역(2026-09-28)
export const listDeliverables = () => req<{ deliverables: PjDeliverable[] }>(`${BASE}/deliverables`).then((r) => r.deliverables);

// body = 카드 글(평문, 보내면 여러 메시지 합치기보다 우선 — 기존 카드(cid)는 글 뒤에 덧붙임) · owners = 담당자 userId (비면 본인)
//   copyFile = 기존 카드에 메시지 첨부를 새 버전으로(기본 true) — 2026-09-28
//   fileId = 업로드 id — 파일 미리보기에서 등록(2026-11-02). 메시지 대표 첨부 대신 그 파일을 복사하고, msgId 가 비면 방은 업로드의 방
export const registerMessage = (body: { pid: string; sid?: string; cid?: string; rid: string; msgId: string; msgIds?: string[]; fileId?: string; title?: string; body?: string; owners?: string[]; copyFile?: boolean }) =>
	req<{ ok: boolean; cid: string; ver: number; appended?: boolean; appendErr?: string }>(`${BASE}/register-message`, { method: 'POST', body: JSON.stringify(body) });
/** ✨ AI 요약-제목 — 고른 메시지(같은 방)를 카드 제목 한 줄로 (2026-09-28) */
export const summarizeTitle = (body: { pid: string; rid: string; msgIds: string[] }) =>
	req<{ ok: boolean; title: string }>(`${BASE}/summarize-title`, { method: 'POST', body: JSON.stringify(body) }).then((r) => r.title);

// ── 🔑 업무 키 목록 (2026-09-06 2차-B) — 채팅 본문 'MT-12' 자동 링크가 쓰는 사전 ──────────
/** 내가 멤버인 **활성·키 있는** 프로젝트 1줄 (`GET /keys` → `{ keys: [...] }`) */
export interface PjKeyItem { key: string; pid: string; name: string; color: string }

const KEYS_TTL_MS = 60_000;
let _keys: PjKeyItem[] = [];
let _keysAt = 0;
let _keysInflight: Promise<PjKeyItem[]> | null = null;

/** 지금 알고 있는 키 목록 — **동기**. 아직 못 받았으면 빈 배열(자동 링크를 그리지 않을 뿐, 무해) */
export function projectKeysSnapshot(): PjKeyItem[] { return _keys; }

/** 60초 캐시 조회. 실패는 조용히 무시(자동 링크는 있으면 좋은 기능) — 로그인 직후 1회 프리로드에 쓴다 */
export async function fetchProjectKeys(): Promise<PjKeyItem[]> {
	if (_keysAt && Date.now() - _keysAt < KEYS_TTL_MS) return _keys;
	if (_keysInflight) return _keysInflight;
	_keysInflight = req<{ keys?: PjKeyItem[] }>(`${BASE}/keys`)
		.then((r) => {
			_keys = (r.keys || []).filter((k) => !!k && !!k.key && !!k.pid);
			_keysAt = Date.now();
			return _keys;
		})
		.catch(() => _keys)          // 구 사이드카(404)·네트워크 오류 — 종전 목록 유지
		.finally(() => { _keysInflight = null; });
	return _keysInflight;
}

/** 프로젝트 생성·키 변경 직후 — 다음 조회가 서버를 다시 보게 한다 */
export function invalidateProjectKeys(): void { _keysAt = 0; }

/** [TPT_PJT]{json} 공유 카드 파서 — MessageRow·SidebarTree 공용 */
export interface PjtCardPayload { v: number; kind: 'card'; pid: string; cid: string; title: string; pjName: string; stage?: string; status?: string; type?: string; ver?: number }
export function parsePjtCard(msg: string | undefined): PjtCardPayload | null {
	if (!msg || !msg.startsWith('[TPT_PJT]')) return null;
	try {
		const p = JSON.parse(msg.slice('[TPT_PJT]'.length)) as PjtCardPayload;
		return p && p.pid && p.cid ? p : null;
	} catch { return null; }
}

/** 주(일~토) 시작·끝 날짜 — 단계 일정 프리필용 (YYYY-MM-DD) */
export function weekRange(base = new Date()): { from: string; to: string } {
	const d = new Date(base);
	const sun = new Date(d); sun.setDate(d.getDate() - d.getDay());          // 일요일 시작 (2026-08-29 사용자)
	const sat = new Date(sun); sat.setDate(sun.getDate() + 6);
	const f = (x: Date) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
	return { from: f(sun), to: f(sat) };
}

/** 이번 주차 라벨 — 'N주차(M/D~M/D)' KST 기준, 일~토 (2026-08-29 사용자). */
//   서버 잡과 같은 함수(pjtPure.kstWeekLabel)를 쓴다 — 구식 ceil 계산은 토요일에 번호가 +1 로 넘어갔다 (2026-10-05)
export function weekStageLabel(base = new Date()): string {
	return kstWeekLabel(base);
}
